import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { useSelector } from "react-redux";

// Icons
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";

// Components
import CircularProgresss from "views/components/CircularProgress/CircularProgress";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import Button from "../../components/CustomButtons/Button";
import BottomButton from "components/UpdateUi/BottomButton/BottomButton";
import ToastModal from "views/components/ToastModal";

// API / Utils
import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";
import { toCamelCase, parseDateMDY } from "utils/Utils";
import { status } from "assets/properties/properties";

const useStyles = makeStyles((theme) => ({
  container: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "85vh",
    overflow: "hidden",
    background: "#F7F9FC",
  },

  content: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "24px 32px 10px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 14px 100px",
    },
  },

  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 22,

    [theme.breakpoints.down("sm")]: {
      marginBottom: 16,
    },
  },

  pageTitleWrapper: {
    display: "flex",
    alignItems: "center",
  },

  pageIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #E9FFF4 0%, #DDF8EB 100%)",
    marginRight: 14,
    color: "#159B5D",

    [theme.breakpoints.down("sm")]: {
      width: 42,
      height: 42,
      borderRadius: 12,
    },
  },

  pageTitle: {
    fontSize: 27,
    fontWeight: 700,
    color: "#14264D",
    lineHeight: 1.2,

    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
    },
  },

  pageSubtitle: {
    color: "#6B7280",
    fontSize: 14,
    marginTop: 5,
  },

  helpCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    background: "#FFFFFF",
    border: "1px solid #E8EDF3",
    borderRadius: 12,
    boxShadow: "0 3px 12px rgba(20, 38, 77, 0.05)",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  helpIcon: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#F1EBFF",
    color: "#7856D9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  helpTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1D2B53",
  },

  helpSubtitle: {
    fontSize: 11,
    color: "#7A8499",
    marginTop: 2,
  },

  /* ---------------- Instructions ---------------- */

  instructionCard: {
    position: "relative",
    background: "linear-gradient(135deg, #F7FFFA 0%, #FFFFFF 100%)",
    border: "1px solid #D8F1E3",
    borderRadius: 16,
    padding: "24px 28px",
    marginBottom: 22,
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      padding: 18,
    },
  },

  instructionContent: {
    display: "flex",
    alignItems: "center",

    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
    },
  },

  instructionIconWrapper: {
    width: 76,
    height: 76,
    minWidth: 76,
    borderRadius: "50%",
    background: "#E3F9ED",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 22,
    border: "8px solid #F0FCF5",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  instructionIcon: {
    fontSize: 34,
    color: "#15A866",
  },

  instructionDivider: {
    width: 1,
    height: 105,
    background: "#D7EDE0",
    marginRight: 22,

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  instructionTitle: {
    color: "#079653",
    fontSize: 17,
    fontWeight: 700,
    marginBottom: 14,
  },

  instructionItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: 9,

    "&:last-child": {
      marginBottom: 0,
    },
  },

  instructionCheck: {
    color: "#14A866",
    fontSize: 19,
    marginRight: 9,
    marginTop: 1,
  },

  instructionText: {
    fontSize: 13.5,
    color: "#4B5563",
    lineHeight: 1.5,
  },

  instructionVisual: {
    width: 170,
    height: 120,
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  documentVisual: {
    position: "relative",
    width: 100,
    height: 82,
    borderRadius: 14,
    background: "#DFF7E9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "rotate(-3deg)",
  },

  documentIcon: {
    fontSize: 48,
    color: "#17A866",
  },

  securityBadge: {
    position: "absolute",
    right: -12,
    bottom: -8,
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "#FFFFFF",
    boxShadow: "0 3px 12px rgba(0,0,0,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  securityIcon: {
    color: "#16A765",
    fontSize: 25,
  },

  /* ---------------- Main Empty State ---------------- */

  emptyCard: {
    background: "#FFFFFF",
    border: "1px solid #E7EBF1",
    borderRadius: 18,
    minHeight: 350,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxShadow: "0 5px 22px rgba(28, 44, 78, 0.05)",
    padding: "35px 20px",
  },

  emptyContent: {
    maxWidth: 620,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  bankIllustration: {
    position: "relative",
    width: 145,
    height: 115,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  bankBuilding: {
    width: 92,
    height: 72,
    background: "#EEF2FA",
    borderRadius: "5px 5px 8px 8px",
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-evenly",
    padding: "0 10px 9px",
    boxSizing: "border-box",
  },

  bankRoof: {
    position: "absolute",
    top: -18,
    left: -8,
    width: 108,
    height: 25,
    background: "#DCE3F1",
    clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
  },

  bankColumn: {
    width: 8,
    height: 43,
    borderRadius: 2,
    background: "#C8D2E5",
  },

  searchCircle: {
    position: "absolute",
    right: 10,
    bottom: 5,
    width: 50,
    height: 50,
    borderRadius: "50%",
    background: "#FFFFFF",
    border: "5px solid #22B875",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 5px 15px rgba(30, 170, 105, 0.15)",
  },

  searchIcon: {
    color: "#22B875",
    fontSize: 27,
  },

  emptyTitle: {
    fontSize: 21,
    fontWeight: 700,
    color: "#14264D",
    marginBottom: 8,

    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
    },
  },

  emptySubtitle: {
    fontSize: 14,
    color: "#68758B",
    lineHeight: 1.7,
    maxWidth: 580,
    marginBottom: 20,
  },

  createButton: {
    minWidth: 225,
    height: 48,
    borderRadius: 8,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 700,
    color: "#FFFFFF !important",
    background:
      "linear-gradient(135deg, #123B56 0%, #13A765 55%, #20C77B 100%) !important",
    boxShadow: "0 7px 18px rgba(19, 167, 101, 0.22)",
    transition: "all 0.2s ease",

    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 22px rgba(19, 167, 101, 0.28)",
    },
  },

  createButtonIcon: {
    marginRight: 8,
    fontSize: 20,
  },

  /* ---------------- Pending State ---------------- */

  pendingCard: {
    background: "#FFFFFF",
    border: "1px solid #E7EBF1",
    borderRadius: 18,
    minHeight: 350,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxShadow: "0 5px 22px rgba(28, 44, 78, 0.05)",
    padding: 30,
  },

  pendingIcon: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    background: "#ECFDF3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
    color: "#16A765",
  },

  pendingTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#14264D",
    marginBottom: 10,
  },

  pendingText: {
    maxWidth: 570,
    color: "#68758B",
    fontSize: 14,
    lineHeight: 1.7,
    marginBottom: 20,
  },

  /* ---------------- Completed Details ---------------- */

  detailsCard: {
    background: "#FFFFFF",
    border: "1px solid #E5EAF0",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 5px 22px rgba(28, 44, 78, 0.05)",
  },

  detailsHeader: {
    padding: "22px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #EDF0F4",

    [theme.breakpoints.down("sm")]: {
      padding: 16,
    },
  },

  detailsHeaderLeft: {
    display: "flex",
    alignItems: "center",
  },

  detailsIcon: {
    width: 50,
    height: 50,
    borderRadius: 13,
    background: "#EAF9F1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 13,
    color: "#16A765",
  },

  detailsTitle: {
    fontSize: 21,
    fontWeight: 700,
    color: "#14264D",
  },

  detailsSubtitle: {
    fontSize: 13,
    color: "#7A8499",
    marginTop: 3,
  },

  verifiedBadge: {
    display: "flex",
    alignItems: "center",
    background: "#ECFDF3",
    color: "#078B4C",
    borderRadius: 20,
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 700,

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  verifiedIcon: {
    fontSize: 17,
    marginRight: 5,
  },

  detailsBody: {
    padding: 24,

    [theme.breakpoints.down("sm")]: {
      padding: 16,
    },
  },

  detailItem: {
    background: "#F9FAFC",
    border: "1px solid #EDF0F4",
    borderRadius: 10,
    padding: "13px 14px",
    minHeight: 72,
    boxSizing: "border-box",
  },

  detailLabel: {
    color: "#8993A4",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    marginBottom: 6,
  },

  detailValue: {
    color: "#1D2B53",
    fontSize: 14,
    fontWeight: 700,
    wordBreak: "break-word",
  },

  mismatchYes: {
    color: "#DC2626",
  },

  mismatchNo: {
    color: "#159B5D",
  },

  /* ---------------- Security ---------------- */

  securityCard: {
    marginTop: 18,
    padding: "15px 18px",
    borderRadius: 12,
    background: "#F8FAFF",
    border: "1px solid #E4EAF5",
    display: "flex",
    alignItems: "center",
  },

  securityCardIcon: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: "50%",
    background: "#E9F8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    color: "#159B5D",
  },

  securityTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1D2B53",
  },

  securityText: {
    fontSize: 12,
    color: "#748096",
    marginTop: 3,
  },

  /* ---------------- Footer ---------------- */

  bottomArea: {
    width: "100%",
    background: "#FFFFFF",
    borderTop: "1px solid #E8ECF2",
  },

  /* ---------------- Responsive ---------------- */

  mobileFullWidth: {
    width: "100%",
  },
}));

const BankAnalyserLink = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLead = useSelector(
    (state) => state.login.userCurrentLead
  );

  const authData = useSelector((state) => state.login.authData);

  const { mobileNumber } = useSelector(
    (state) => state.login.userData
  );

  const { leadId } = currentLead || {};
  const { authToken } = authData || {};

  const pollingRef = useRef(null);

  const [callInProgress, setCallInProgress] = useState(false);
  const [data, setData] = useState({});
  const [mandateData, setMandateData] = useState(false);
  const [isMandatePending, setIsMandatePending] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");
  const [requestId, setRequestId] = useState("");
  const [bankDetails, setBankDetails] = useState(null);

  const toastUpdate = (type, message, showToast = true) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return;

    pollingRef.current = setInterval(() => {
      fetchLoanSummary();
    }, 5000);
  };

  useEffect(() => {
    if (leadId) {
      fetchLoanSummary();
    }

    return () => stopPolling();
  }, [leadId]);

  useEffect(() => {
    if (isMandatePending) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [isMandatePending]);

  useEffect(() => {
    if (!location.search) return;

    fetchLoanSummary();
  }, [location.search]);

  const fetchLoanSummary = async () => {
    setCallInProgress(true);

    try {
      const result = await apiHandler({
        url: endpoint.USER_BANK_ANALYSER_GET_API + leadId,
        method: "GET",
        authToken,
      });

      setCallInProgress(false);

      if (result?.data?.error_code) {
        return;
      }

      if (
        result?.data?.status?.toLowerCase() === status.completed
      ) {
        setBankDetails(result.data);
        setMandateData(true);
        setIsMandatePending(false);

        if (result.data.nameMismatch) {
          toastUpdate( "Error", "Applicant name does not match the bank statement.", true);
          setMandateData(false);
          setIsMandatePending(false);
          return;
        }
      }
    } finally {
      setCallInProgress(false);
    }
  };

  const createMandateDetails = async () => {
    setCallInProgress(true);

    try {
      const result = await apiHandler({
        url: endpoint.ACCOUNT_AGGREGRATOR_INITIATE,
        method: "POST",
        authToken,
        data: {
          leadApplicationId: leadId,
          mobileNumber,
          returnUrl: window.location.origin + `/journey/BankAnalyser`,
        },
      });

      const response = result?.data;

      if (response?.error_code) {
        toastUpdate(
          "Error",
          response?.user_description,
          true
        );
        return;
      }

      if (response?.requestId) {
        setRequestId(response.requestId);
      }

      setIsMandatePending(true);

      if (response?.redirectUrl) {
        window.location.href = response.redirectUrl;
      }
    } finally {
      setCallInProgress(false);
    }
  };

  const handleNext = async () => {
    setCallInProgress(true);

    try {
      const apiData = {
        isBankStatementRequired: true,
      };

      const result = await apiHandler({
        url: endpoint.GET_CUSTOMER_ELIGIBILITY + leadId,
        method: "POST",
        authToken,
        data: apiData,
      });

      if (result?.data?.error_code) {
        toastUpdate(
          "Error",
          result.data.user_description,
          true
        );
        return;
      }

      switch (
        result?.data?.decision?.trim().toLowerCase()
      ) {
        case "reject":
        case "rejected":
          navigate("/loanRejected");
          break;

        case "approve":
        case "approved":
          navigate("/journey/SelfiePhoto");
          break;

        default:
          toastUpdate(
            "Error",
            "We couldn't process your loan decision. Please try again later.",
            true
          );
      }
    } catch (error) {
      toastUpdate(
        "Error",
        "Something went wrong. Please try again later.",
        true
      );
    } finally {
      setCallInProgress(false);
    }
  };

  const isMandateValid = () => {
    return (
      mandateData &&
      bankDetails?.status?.toLowerCase() === "completed"
    );
  };

  const instructions = [
    "Upload a single PDF containing your last 3 months' bank statement.",
    "Ensure the statement includes transactions up to today's date.",
    "If the PDF is password-protected, enter the password below.",
    "Make sure all pages are clear and readable.",
  ];

  const bankFields = [
    {
      label: "Bank Account No",
      value: bankDetails?.bankAccountNo,
    },
    {
      label: "IFSC Code",
      value: bankDetails?.ifscCode,
    },
    {
      label: "Bank Name",
      value: toCamelCase(bankDetails?.bankName),
    },
    {
      label: "Branch",
      value: toCamelCase(bankDetails?.branch),
    },
    {
      label: "Beneficiary Name",
      value: toCamelCase(bankDetails?.beneficiaryName),
    },
    {
      label: "Account Type",
      value: toCamelCase(bankDetails?.accountType),
    },
    {
      label: "Txn Start Date",
      value: parseDateMDY(bankDetails?.txnStartDate),
    },
    {
      label: "Txn End Date",
      value: parseDateMDY(bankDetails?.txnEndDate),
    },
    {
      label: "Last Salary Credited",
      value: parseDateMDY(
        bankDetails?.lastSalaryCreditedDate
      ),
    },
    {
      label: "Name Mismatch",
      value: bankDetails?.nameMismatch ? "Yes" : "No",
    },
  ];

  return (
    <Grid container className={classes.container}>
      {/* Existing application header */}
      <Grid item xs={12}>
        <SubHeader label="Income Verify" />
      </Grid>
      <Grid item xs={12} className={classes.content}>
        {!mandateData && (
          <Box className={classes.instructionCard}>
            <Box className={classes.instructionContent}>
              <Box>
                <Typography className={classes.instructionTitle}>
                  Document Upload Guidelines
                </Typography>

                {instructions.map((item, index) => (
                  <Box
                    key={index}
                    className={classes.instructionItem}
                  >
                    <CheckCircleIcon
                      className={classes.instructionCheck}
                    />

                    <Typography
                      className={classes.instructionText}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
        {mandateData ? (
          <Box className={classes.detailsCard}>
            <Box className={classes.detailsHeader}>
              <Box className={classes.detailsHeaderLeft}>
                <Box className={classes.detailsIcon}>
                  <AccountBalanceIcon />
                </Box>

                <Box>
                  <Typography className={classes.detailsTitle}>
                    Bank Analysis Details
                  </Typography>

                  <Typography className={classes.detailsSubtitle}>
                    Verified bank statement information
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.verifiedBadge}>
                <VerifiedUserOutlinedIcon
                  className={classes.verifiedIcon}
                />
                Verified
              </Box>
            </Box>

            <Box className={classes.detailsBody}>
              <Grid container spacing={2}>
                {bankFields.map((item, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={index}
                  >
                    <Box className={classes.detailItem}>
                      <Typography
                        className={classes.detailLabel}
                      >
                        {item.label}
                      </Typography>

                      <Typography
                        className={`${classes.detailValue} ${
                          item.label === "Name Mismatch"
                            ? item.value === "Yes"
                              ? classes.mismatchYes
                              : classes.mismatchNo
                            : ""
                        }`}
                      >
                        {item.value || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box className={classes.securityCard}>
                <Box className={classes.securityCardIcon}>
                  <LockOutlinedIcon />
                </Box>

                <Box>
                  <Typography className={classes.securityTitle}>
                    Your bank data is secure
                  </Typography>

                  <Typography className={classes.securityText}>
                    Your information is protected using secure
                    bank-level encryption.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : isMandatePending ? (
          /* Pending */
          <Box className={classes.pendingCard}>
            <Box className={classes.emptyContent}>
              <Box className={classes.pendingIcon}>
                <AccountBalanceIcon style={{ fontSize: 36 }} />
              </Box>

              <Typography className={classes.pendingTitle}>
                Bank Analyzer request is in progress
              </Typography>

              <Typography className={classes.pendingText}>
                Please complete the authorization in the newly
                opened browser tab or from the link received on
                your registered mobile number.
              </Typography>

              <Button
                color="primary"
                className={classes.createButton}
                onClick={createMandateDetails}
              >
                <SearchOutlinedIcon
                  className={classes.createButtonIcon}
                />
                Refresh Status
              </Button>
            </Box>
          </Box>
        ) : (
          /* Empty */
          <Box className={classes.emptyCard}>
            <Box className={classes.emptyContent}>
              <Box className={classes.bankIllustration}>
                <Box className={classes.bankBuilding}>
                  <Box className={classes.bankRoof} />

                  <Box className={classes.bankColumn} />
                  <Box className={classes.bankColumn} />
                  <Box className={classes.bankColumn} />
                  <Box className={classes.bankColumn} />
                </Box>

                <Box className={classes.searchCircle}>
                  <SearchOutlinedIcon
                    className={classes.searchIcon}
                  />
                </Box>
              </Box>

              <Typography className={classes.emptyTitle}>
                No Bank Analyzer details available
              </Typography>

              <Typography className={classes.emptySubtitle}>
                Click the button below to initiate the Account
                Aggregator journey
                <br />
                and securely fetch your bank account data.
              </Typography>

              <Button
                color="primary"
                className={classes.createButton}
                onClick={createMandateDetails}
              >
                <AccountBalanceIcon
                  className={classes.createButtonIcon}
                />
                Create Bank Analyzer
              </Button>
            </Box>
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <BottomButton
          button1="Next"
          button1click={handleNext}
          disabled1={!isMandateValid()}
        />
      </Grid>

      {/* Loader */}
      {callInProgress && (
        <CircularProgresss
          callInProgress={callInProgress}
        />
      )}

      {/* Toast */}
      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}
    </Grid>
  );
};

export default BankAnalyserLink;